import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Video, MessageSquare, Loader2, Plus, Save, Trash2, ImageIcon, Sparkles } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";

const CATEGORY_OPTIONS = [
    { value: "autocuidado", label: "Autocuidado" },
    { value: "cuidados_idoso", label: "Cuidados com a pessoa idosa" },
    { value: "longevidade", label: "Longevidade" },
    { value: "estilo_vida", label: "Medicina do estilo de vida" },
    { value: "espiritualidade", label: "Espiritualidade" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContentAdminPage() {
    const [tab, setTab] = useState("moderation");
    const [pending, setPending] = useState({ comments: [], testimonials: [] });
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try {
            const [m, p] = await Promise.all([
                api.get("/moderation/pending"),
                api.get("/blog/posts", { params: { published_only: false } }),
            ]);
            setPending({ comments: m.data.comments, testimonials: m.data.testimonials });
            setPosts(p.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const moderate = async (type, id, action) => {
        try {
            const endpoint = type === "comment" ? `/moderation/comment/${id}` : `/moderation/testimonial/${id}`;
            await api.put(endpoint, { action });
            toast.success(action === "approve" ? "Aprovado — agora público" : "Rejeitado");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell>
            <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Comunicação com pacientes</div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Minuto da Saúde — administração</h1>
            </div>

            <div className="border-b border-[rgba(30,63,51,0.1)] mb-6 flex flex-wrap gap-1">
                {[
                    { id: "moderation", label: `Moderação (${pending.comments.length + pending.testimonials.length})`, Icon: MessageSquare },
                    { id: "posts", label: `Publicações (${posts.length})`, Icon: Sparkles },
                ].map((t) => {
                    const active = tab === t.id;
                    const N = t.Icon;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`content-tab-${t.id}`}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 -mb-px ${active ? "border-brand text-brand" : "border-transparent text-muted-clinical hover:text-brand"}`}>
                            <N size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {loading ? <p className="text-muted-clinical">Carregando…</p> : (
                <>
                    {tab === "moderation" && (
                        <div className="space-y-6">
                            <ModerationSection
                                title="Comentários pendentes"
                                items={pending.comments}
                                emptyLabel="Nenhum comentário aguardando aprovação."
                                render={(c) => (
                                    <>
                                        <div className="text-xs text-muted-clinical mb-1">em: <em>{c.post_title || "—"}</em></div>
                                        <div className="font-medium text-brand">{c.author_name}</div>
                                        <p className="text-sm text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">{c.body}</p>
                                    </>
                                )}
                                onApprove={(id) => moderate("comment", id, "approve")}
                                onReject={(id) => moderate("comment", id, "reject")}
                            />
                            <ModerationSection
                                title="Depoimentos pendentes"
                                items={pending.testimonials}
                                emptyLabel="Nenhum depoimento aguardando aprovação."
                                render={(t) => (
                                    <>
                                        <div className="font-medium text-brand">{t.author_name}</div>
                                        {t.author_email && <div className="text-xs text-muted-clinical">{t.author_email}</div>}
                                        <p className="text-sm text-[var(--text-secondary)] mt-2 whitespace-pre-wrap">{t.body}</p>
                                        {t.video_url && (
                                            t.video_url.startsWith("/api") ? (
                                                <video controls src={`${BACKEND_URL}${t.video_url}`} className="w-full rounded-lg bg-black mt-3 max-h-64" />
                                            ) : (
                                                <div className="text-xs text-brand-secondary mt-2 flex items-center gap-1"><Video size={12} /> {t.video_url}</div>
                                            )
                                        )}
                                    </>
                                )}
                                onApprove={(id) => moderate("testimonial", id, "approve")}
                                onReject={(id) => moderate("testimonial", id, "reject")}
                            />
                        </div>
                    )}
                    {tab === "posts" && (
                        <PostsAdmin
                            posts={posts}
                            reload={load}
                            showForm={showForm}
                            setShowForm={setShowForm}
                            editing={editing}
                            setEditing={setEditing}
                        />
                    )}
                </>
            )}
        </AppShell>
    );
}

function ModerationSection({ title, items, emptyLabel, render, onApprove, onReject }) {
    return (
        <div>
            <h3 className="font-display text-lg font-medium text-brand mb-3">{title} <span className="text-sm text-muted-clinical">({items.length})</span></h3>
            {items.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical">{emptyLabel}</div>
            ) : (
                <div className="space-y-3">
                    {items.map((it) => (
                        <div key={it.id} data-testid={`mod-item-${it.id}`} className="card-earthy border-l-4 border-[var(--status-warning)]">
                            {render(it)}
                            <div className="text-[10px] uppercase tracking-wider text-muted-clinical mt-3">{new Date(it.created_at).toLocaleString("pt-BR")}</div>
                            <div className="flex gap-2 mt-3">
                                <button data-testid={`approve-${it.id}`} onClick={() => onApprove(it.id)} className="btn-primary text-sm"><Check size={14} /> Aprovar</button>
                                <button data-testid={`reject-${it.id}`} onClick={() => onReject(it.id)} className="btn-secondary text-sm text-[var(--status-danger)]"><X size={14} /> Rejeitar</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function PostsAdmin({ posts, reload, showForm, setShowForm, editing, setEditing }) {
    const startNew = () => { setEditing({ title: "", category: "autocuidado", excerpt: "", body: "", cover_image_url: "", video_url: "", published: true }); setShowForm(true); };
    const startEdit = (p) => { setEditing({ ...p }); setShowForm(true); };
    const remove = async (id) => {
        if (!window.confirm("Excluir esta publicação?")) return;
        try { await api.delete(`/blog/posts/${id}`); toast.success("Publicação excluída"); reload(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div>
            {!showForm && (
                <>
                    <div className="mb-4"><button data-testid="new-post-btn" onClick={startNew} className="btn-primary"><Plus size={14} /> Nova publicação</button></div>
                    {posts.length === 0 ? (
                        <div className="card-earthy text-sm text-muted-clinical">Nenhuma publicação ainda.</div>
                    ) : (
                        <div className="space-y-2">
                            {posts.map((p) => (
                                <div key={p.id} data-testid={`admin-post-${p.id}`} className="card-earthy flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-brand truncate">{p.title}</div>
                                        <div className="text-xs text-muted-clinical mt-1 flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded-full bg-alt">{p.category}</span>
                                            <span>{p.published ? "Publicado" : "Rascunho"}</span>
                                            <span>{new Date(p.updated_at || p.created_at).toLocaleDateString("pt-BR")}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => startEdit(p)} className="btn-secondary text-sm">Editar</button>
                                        <button onClick={() => remove(p.id)} className="text-[var(--status-danger)] p-2 hover:bg-[var(--status-danger-bg)] rounded"><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
            {showForm && <PostForm post={editing} onCancel={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); reload(); }} />}
        </div>
    );
}

function PostForm({ post, onCancel, onSaved }) {
    const [f, setF] = useState({ ...post });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setF((p) => ({ ...p, cover_image_url: data.url }));
            toast.success("Capa enviada");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (post.id) {
                await api.put(`/blog/posts/${post.id}`, f);
                toast.success("Publicação atualizada");
            } else {
                await api.post("/blog/posts", f);
                toast.success("Publicação criada");
            }
            onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="card-earthy space-y-4 max-w-3xl">
            <h3 className="font-display text-xl font-medium text-brand">{post.id ? "Editar publicação" : "Nova publicação"}</h3>
            <div>
                <label className="label-clinical">Título *</label>
                <input data-testid="post-title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input-clinical" />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="label-clinical">Categoria</label>
                    <select data-testid="post-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="input-clinical">
                        {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-6">
                    <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm">Publicar imediatamente</span>
                </label>
            </div>
            <div>
                <label className="label-clinical">Resumo (chamada da capa)</label>
                <input value={f.excerpt || ""} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className="input-clinical" />
            </div>
            <div>
                <label className="label-clinical">URL de vídeo (YouTube, Vimeo — opcional)</label>
                <input value={f.video_url || ""} onChange={(e) => setF({ ...f, video_url: e.target.value })} className="input-clinical" placeholder="https://youtu.be/..." />
            </div>
            <div>
                <label className="label-clinical">Imagem de capa</label>
                <div className="flex items-center gap-2">
                    <label className="btn-secondary text-sm cursor-pointer">
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} Enviar capa
                        <input data-testid="post-cover-input" type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                    </label>
                    {f.cover_image_url && <span className="text-xs text-brand-secondary">Capa carregada</span>}
                </div>
            </div>
            <div>
                <label className="label-clinical">Corpo do artigo (Markdown)</label>
                <textarea data-testid="post-body" required rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="input-clinical font-mono text-sm" placeholder="## Introdução&#10;&#10;Escreva aqui..." />
            </div>
            <div className="flex gap-2">
                <button data-testid="post-save" disabled={saving} className="btn-primary"><Save size={14} /> {post.id ? "Atualizar" : "Publicar"}</button>
                <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
            </div>
        </form>
    );
}
